import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../utils/api-client';

interface Integration {
  id: string;
  name: string;
  description: string;
  category: 'publishing' | 'analytics' | 'crm' | 'automation' | 'payment';
  icon: string;
  status: 'available' | 'connected' | 'coming-soon';
  connected: boolean;
  features: string[];
}

const integrations: Integration[] = [
  {
    id: 'wordpress',
    name: 'WordPress',
    description: 'Publish content directly to your WordPress site',
    category: 'publishing',
    icon: '📝',
    status: 'available',
    connected: false,
    features: ['One-click publishing', 'Auto-sync content', 'Schedule posts'],
  },
  {
    id: 'medium',
    name: 'Medium',
    description: 'Cross-post to Medium for wider reach',
    category: 'publishing',
    icon: '✍️',
    status: 'available',
    connected: false,
    features: ['Auto-publish', 'Format conversion', 'Tag management'],
  },
  {
    id: 'ghost',
    name: 'Ghost',
    description: 'Publish to Ghost CMS',
    category: 'publishing',
    icon: '👻',
    status: 'available',
    connected: false,
    features: ['Native integration', 'SEO optimization', 'Member management'],
  },
  {
    id: 'google-analytics',
    name: 'Google Analytics',
    description: 'Sync analytics data with Google Analytics',
    category: 'analytics',
    icon: '📊',
    status: 'available',
    connected: false,
    features: ['Event tracking', 'Conversion goals', 'Custom dimensions'],
  },
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Automate workflows with 5000+ apps',
    category: 'automation',
    icon: '⚡',
    status: 'available',
    connected: false,
    features: ['Webhooks', 'Triggers', 'Actions'],
  },
  {
    id: 'make',
    name: 'Make (Integromat)',
    description: 'Build complex automation workflows',
    category: 'automation',
    icon: '🔧',
    status: 'available',
    connected: false,
    features: ['Visual workflows', 'Data transformation', 'Error handling'],
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Process payments and subscriptions',
    category: 'payment',
    icon: '💳',
    status: 'connected',
    connected: true,
    features: ['Payment processing', 'Subscription management', 'Invoicing'],
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Sync leads and contacts to HubSpot CRM',
    category: 'crm',
    icon: '🎯',
    status: 'coming-soon',
    connected: false,
    features: ['Contact sync', 'Deal tracking', 'Email campaigns'],
  },
];

export function IntegrationsPage(): ReactNode {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = ['all', 'publishing', 'analytics', 'automation', 'payment', 'crm'];
  const categoryLabels: Record<string, string> = {
    all: 'All Integrations',
    publishing: 'Publishing',
    analytics: 'Analytics',
    automation: 'Automation',
    payment: 'Payment',
    crm: 'CRM',
  };

  const filteredIntegrations = integrations.filter((integration) => {
    if (selectedCategory !== 'all' && integration.category !== selectedCategory) return false;
    if (searchQuery && !integration.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !integration.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const connectedCount = integrations.filter(i => i.connected).length;
  const availableCount = integrations.filter(i => i.status === 'available').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Integrations</h2>
          <p className="text-gray-500">
            Connect ProfitOS with your favorite tools and services
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Integrations</h3>
          <p className="mt-2 text-3xl font-bold">{integrations.length}</p>
        </div>
        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-sm font-medium text-gray-500">Connected</h3>
          <p className="mt-2 text-3xl font-bold text-green-600">{connectedCount}</p>
        </div>
        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-sm font-medium text-gray-500">Available</h3>
          <p className="mt-2 text-3xl font-bold">{availableCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search integrations..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); }}
          className="flex-1 rounded-md border border-gray-300 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <div className="flex gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`rounded-md px-4 py-2 text-sm font-medium ${
                selectedCategory === cat
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {categoryLabels[cat]}
            </button>
          ))}
        </div>
      </div>

      {/* Integrations Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredIntegrations.map((integration) => (
          <IntegrationCard key={integration.id} integration={integration} />
        ))}
      </div>

      {filteredIntegrations.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No integrations found</h3>
          <p className="mt-2 text-gray-500">Try adjusting your search or filters.</p>
        </div>
      )}
    </div>
  );
}

function IntegrationCard({ integration }: { integration: Integration }): ReactNode {
  const connectMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      // This would call POST /integrations/{id}/connect
      await api.post(`/integrations/${integration.id}/connect`, {});
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      await api.post(`/integrations/${integration.id}/disconnect`, {});
    },
  });

  return (
    <div className={`rounded-lg border p-6 ${
      integration.connected ? 'border-green-200 bg-green-50' : 'bg-white'
    }`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="text-3xl">{integration.icon}</div>
          <div>
            <h3 className="font-semibold text-gray-900">{integration.name}</h3>
            {integration.status === 'coming-soon' && (
              <span className="text-xs text-gray-500">Coming Soon</span>
            )}
          </div>
        </div>
        {integration.connected && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Connected
          </span>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-4">{integration.description}</p>

      <div className="mb-4">
        <h4 className="text-xs font-medium text-gray-700 mb-2">Features:</h4>
        <ul className="space-y-1">
          {integration.features.map((feature, i) => (
            <li key={i} className="text-xs text-gray-600 flex items-center gap-1">
              <svg className="h-3 w-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {feature}
            </li>
          ))}
        </ul>
      </div>

      {integration.status === 'coming-soon' ? (
        <button
          disabled
          className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-400 cursor-not-allowed"
        >
          Coming Soon
        </button>
      ) : integration.connected ? (
        <button
          onClick={() => void disconnectMutation.mutate()}
          className="w-full rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
        >
          Disconnect
        </button>
      ) : (
        <button
          onClick={() => void connectMutation.mutate()}
          className="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Connect
        </button>
      )}
    </div>
  );
}
